import { useMutation, useQuery } from '@apollo/client';
import { useI18n } from '@i18n';
import { Co2PriceCurveConfig, SetOrgCo2PriceCurveInput } from '@predium/client-graphql';
import { crrem_version_type_enum, emission_factor_type_enum, eu_taxonomy_datasource_enum } from '@predium/enums';
import { useSnackbar } from 'notistack';
import { createContext, PropsWithChildren, useCallback, useMemo } from 'react';
import { sessionStore } from '@/components/session/session.store';
import { useSession } from '@/components/session/useSession';
import { SnackbarTimeouts } from '../../components/NotistackProvider';
import {
  SET_ORG_CO2_PRICE_CURVE,
  UPDATE_ORG_PREFERENCE_FOR_AUTOMATIC_ACTION_COST_UPDATE,
  UPDATE_ORG_PREFERENCE_FOR_ECONOMIC_UNIT,
  UPDATE_ORG_PREFERENCE_FOR_ECONOMIC_UNIT_IN_FILE_DRIVE,
} from '../../graphql/common.mutations';
import { GET_ORG_PREFERENCES } from '../../graphql/common.queries';
import { CRREMConfiguration } from './CRREMConfiguration';

type BasePreferences = {
  isAutomaticActionCostUpdateToggled: boolean;
  isEconomicUnitsInFileDriveToggled: boolean;
  /** Per-org CO₂ price (€/t) development definition. Null = use the country default curve. */
  co2PriceCurveConfig: Co2PriceCurveConfig | null;
  /** Country default CO₂ price (€/t) curve (2020–2050), served by the backend for the editor. */
  co2PriceDefaultCurve: number[];
  crremConfigurations: CRREMConfiguration[];
  availableReportingYears: number[];
  isSSOEnabled: boolean;
  isSSOAutoCreateUser: boolean;
  /** Org-level data source used for EU Taxonomy compliance classification. */
  euTaxonomyDatasource: eu_taxonomy_datasource_enum;
};

export type CrremOrgPreferences = {
  emissionFactorType: emission_factor_type_enum.CRREM;
  crremVersion: crrem_version_type_enum;
  activeCRREMConfiguration: CRREMConfiguration;
} & BasePreferences;

type NationalLawOrgPreferences = {
  emissionFactorType: emission_factor_type_enum.NATIONAL_ENERGY_LAW;
} & BasePreferences;

export type OrgPreferences = NationalLawOrgPreferences | CrremOrgPreferences;

type OrgPreferencesContextProps = {
  orgPreferences: OrgPreferences;
  isLoading: boolean;
  toggleEconomicUnits: () => void;
  toggleEconomicUnitsInFileDrive: () => void;
  toggleAutomaticActionCostUpdate: () => void;
  /** Persist a CO₂ price curve definition, or clear the override with `{ kind: null }`. */
  setCo2PriceCurveConfig: (input: SetOrgCo2PriceCurveInput) => void;
  hasCrremConfig: boolean;
  hasNationalEnergyLawConfig: boolean;
};

const initialState: OrgPreferencesContextProps = {
  toggleEconomicUnits: () => {},
  toggleEconomicUnitsInFileDrive: () => {},
  toggleAutomaticActionCostUpdate: () => {},
  setCo2PriceCurveConfig: () => {},
  orgPreferences: {
    // Start with National Energy Law as default, because CRREM has a sub-selection of emission factors
    emissionFactorType: emission_factor_type_enum.NATIONAL_ENERGY_LAW,
    isAutomaticActionCostUpdateToggled: false,
    isEconomicUnitsInFileDriveToggled: true,
    co2PriceCurveConfig: null,
    co2PriceDefaultCurve: [],
    crremConfigurations: [],
    availableReportingYears: [],
    isSSOEnabled: false,
    isSSOAutoCreateUser: false,
    euTaxonomyDatasource: eu_taxonomy_datasource_enum.DEFAULT_INDEX,
  },
  isLoading: true,
  hasCrremConfig: false,
  hasNationalEnergyLawConfig: true,
};

const OrgPreferencesContext = createContext<OrgPreferencesContextProps>(initialState);

function OrgPreferencesContextProvider({ children }: PropsWithChildren) {
  const { t } = useI18n();
  const {
    org: { isEconomicUnitsToggled },
  } = useSession();
  const { enqueueSnackbar } = useSnackbar();

  const { data, loading } = useQuery(GET_ORG_PREFERENCES, {
    fetchPolicy: 'cache-and-network',
  });

  const orgPreferences = useMemo(() => {
    if (!data) {
      return initialState.orgPreferences;
    }

    const orgPreferencesFromServer = data.getOrgPreferences;
    const activeEmissionFactorPreferences = orgPreferencesFromServer.activeEmissionFactorPreferences;
    const rawCrremConfigurations = orgPreferencesFromServer.crremConfigurations;
    const crremConfigurations = rawCrremConfigurations.map(CRREMConfiguration.fromGraphQL);

    if (activeEmissionFactorPreferences?.__typename === 'CrremOrgPreferences') {
      const activeCRREMConfiguration = activeEmissionFactorPreferences.activeCRREMConfiguration;
      return {
        ...orgPreferencesFromServer,
        co2PriceCurveConfig: orgPreferencesFromServer.co2PriceCurveConfig ?? null,
        crremConfigurations,
        emissionFactorType: emission_factor_type_enum.CRREM,
        crremVersion: activeEmissionFactorPreferences.crremVersion,
        activeCRREMConfiguration: activeCRREMConfiguration
          ? CRREMConfiguration.fromGraphQL(activeCRREMConfiguration)
          : CRREMConfiguration.default(),
      } satisfies OrgPreferences;
    } else {
      return {
        ...orgPreferencesFromServer,
        co2PriceCurveConfig: orgPreferencesFromServer.co2PriceCurveConfig ?? null,
        crremConfigurations,
        emissionFactorType: emission_factor_type_enum.NATIONAL_ENERGY_LAW,
      } satisfies OrgPreferences;
    }
  }, [data]);

  const hasCrremConfig = orgPreferences.emissionFactorType === emission_factor_type_enum.CRREM;
  const hasNationalEnergyLawConfig =
    orgPreferences.emissionFactorType === emission_factor_type_enum.NATIONAL_ENERGY_LAW;

  const [toggleEconomicUnit] = useMutation(UPDATE_ORG_PREFERENCE_FOR_ECONOMIC_UNIT, {
    onError: (_err, options) => {
      // Roll back the optimistic session update on failure.
      const intended = options?.variables?.economicUnitEnabled;
      if (typeof intended === 'boolean') {
        sessionStore.send({ type: 'setIsEconomicUnitsToggled', value: !intended });
      }
      enqueueSnackbar(t('General_FailedToSaveChanges'), {
        variant: 'error',
        autoHideDuration: SnackbarTimeouts.Error,
      });
    },
    onCompleted: () => {
      enqueueSnackbar(t('General_ChangesSavedSuccessfully'), {
        variant: 'success',
        autoHideDuration: SnackbarTimeouts.Success,
      });
    },
  });

  // TODO for later: refactor all mutations into separate components
  const [toggleAutomaticActionCostUpdateMutation] = useMutation(
    UPDATE_ORG_PREFERENCE_FOR_AUTOMATIC_ACTION_COST_UPDATE,
    {
      onError: () => {
        enqueueSnackbar(t('General_FailedToSaveChanges'), {
          variant: 'error',
          autoHideDuration: SnackbarTimeouts.Error,
        });
      },
      onCompleted: () => {
        enqueueSnackbar(t('General_ChangesSavedSuccessfully'), {
          variant: 'success',
          autoHideDuration: SnackbarTimeouts.Success,
        });
      },
      refetchQueries: [{ query: GET_ORG_PREFERENCES }],
    },
  );

  const [toggleEconomicUnitsInFileDriveMutation] = useMutation(UPDATE_ORG_PREFERENCE_FOR_ECONOMIC_UNIT_IN_FILE_DRIVE, {
    onError: () => {
      enqueueSnackbar(t('General_FailedToSaveChanges'), {
        variant: 'error',
        autoHideDuration: SnackbarTimeouts.Error,
      });
    },
    onCompleted: () => {
      enqueueSnackbar(t('General_ChangesSavedSuccessfully'), {
        variant: 'success',
        autoHideDuration: SnackbarTimeouts.Success,
      });
    },
    refetchQueries: [{ query: GET_ORG_PREFERENCES }],
  });

  const [setCo2PriceCurveMutation] = useMutation(SET_ORG_CO2_PRICE_CURVE, {
    onError: () => {
      enqueueSnackbar(t('General_FailedToSaveChanges'), {
        variant: 'error',
        autoHideDuration: SnackbarTimeouts.Error,
      });
    },
    onCompleted: () => {
      enqueueSnackbar(t('General_ChangesSavedSuccessfully'), {
        variant: 'success',
        autoHideDuration: SnackbarTimeouts.Success,
      });
    },
    refetchQueries: [{ query: GET_ORG_PREFERENCES }],
  });

  const setCo2PriceCurveConfig = useCallback(
    (input: SetOrgCo2PriceCurveInput) => {
      void setCo2PriceCurveMutation({
        variables: {
          input,
        },
      });
    },
    [setCo2PriceCurveMutation],
  );

  const toggleEconomicUnits = useCallback(() => {
    const nextValue = !isEconomicUnitsToggled;
    sessionStore.send({ type: 'setIsEconomicUnitsToggled', value: nextValue });
    void toggleEconomicUnit({
      variables: {
        economicUnitEnabled: nextValue,
      },
    });
  }, [isEconomicUnitsToggled, toggleEconomicUnit]);

  const toggleAutomaticActionCostUpdate = () => {
    void toggleAutomaticActionCostUpdateMutation({
      variables: {
        automaticActionCostUpdateEnabled: !orgPreferences.isAutomaticActionCostUpdateToggled,
      },
    });
  };

  const toggleEconomicUnitsInFileDrive = () => {
    void toggleEconomicUnitsInFileDriveMutation({
      variables: {
        economicUnitInFileDriveEnabled: !orgPreferences.isEconomicUnitsInFileDriveToggled,
      },
    });
  };

  return (
    <OrgPreferencesContext.Provider
      value={{
        orgPreferences,
        toggleEconomicUnits,
        toggleEconomicUnitsInFileDrive,
        setCo2PriceCurveConfig,
        isLoading: loading,
        toggleAutomaticActionCostUpdate,
        hasCrremConfig,
        hasNationalEnergyLawConfig,
      }}
    >
      {children}
    </OrgPreferencesContext.Provider>
  );
}

/**
 * Shape of the orgPreferences blob carried in shared building snapshots.
 * Mirrors the backend's `OrgPreferenceManagementService.getOrgPreferences` return
 * value after JSON round-tripping (no class instances, no GraphQL `__typename`).
 */
export type SharedSnapshotOrgPreferences = {
  isAutomaticActionCostUpdateToggled: boolean;
  availableReportingYears: number[];
  isSSOEnabled: boolean;
  isSSOAutoCreateUser: boolean;
  isEconomicUnitsToggled: boolean;
  euTaxonomyDatasource?: eu_taxonomy_datasource_enum | null;
  co2PriceCurveConfig?: Co2PriceCurveConfig | null;
  co2PriceDefaultCurve?: number[];
  userLogoutIdleTimeout: number | null;
  // crremConfigurations are CRREMConfigurationObject-shaped without __typename;
  // helper below restores it before handing off to CRREMConfiguration.fromGraphQL.
  crremConfigurations: Array<Record<string, unknown> & { id?: number }>;
  activeEmissionFactorPreferences:
    | { emissionFactorType: emission_factor_type_enum.NATIONAL_ENERGY_LAW }
    | {
        emissionFactorType: emission_factor_type_enum.CRREM;
        crremVersion: crrem_version_type_enum;
        activeCRREMConfiguration: Record<string, unknown> & { id?: number };
      };
};

/**
 * Reconstructs an OrgPreferences object from the JSON-serialized snapshot blob.
 * Adds the `__typename` GraphQL marker (lost during JSON serialization) by
 * inferring it structurally — custom configs carry an `id`, defaults don't —
 * then runs the same transform the live `useQuery` path uses.
 */
function buildOrgPreferencesFromSnapshot(snapshot: SharedSnapshotOrgPreferences): OrgPreferences {
  const tagWithTypename = (config: Record<string, unknown> & { id?: number }) => ({
    ...config,
    __typename:
      'id' in config && config.id !== undefined ? 'CRREMCustomConfigurationObject' : 'CRREMDefaultConfigurationObject',
  });

  const crremConfigurations = snapshot.crremConfigurations.map((c) =>
    CRREMConfiguration.fromGraphQL(tagWithTypename(c) as Parameters<typeof CRREMConfiguration.fromGraphQL>[0]),
  );

  const base: BasePreferences = {
    isAutomaticActionCostUpdateToggled: snapshot.isAutomaticActionCostUpdateToggled,
    isEconomicUnitsInFileDriveToggled: true,
    co2PriceCurveConfig: snapshot.co2PriceCurveConfig ?? null,
    co2PriceDefaultCurve: snapshot.co2PriceDefaultCurve ?? [],
    crremConfigurations,
    availableReportingYears: snapshot.availableReportingYears,
    isSSOEnabled: snapshot.isSSOEnabled,
    isSSOAutoCreateUser: snapshot.isSSOAutoCreateUser,
    euTaxonomyDatasource: snapshot.euTaxonomyDatasource ?? eu_taxonomy_datasource_enum.DEFAULT_INDEX,
  };

  if (snapshot.activeEmissionFactorPreferences.emissionFactorType === emission_factor_type_enum.CRREM) {
    return {
      ...base,
      emissionFactorType: emission_factor_type_enum.CRREM,
      crremVersion: snapshot.activeEmissionFactorPreferences.crremVersion,
      activeCRREMConfiguration: CRREMConfiguration.fromGraphQL(
        tagWithTypename(snapshot.activeEmissionFactorPreferences.activeCRREMConfiguration) as Parameters<
          typeof CRREMConfiguration.fromGraphQL
        >[0],
      ),
    } satisfies CrremOrgPreferences;
  }

  return {
    ...base,
    emissionFactorType: emission_factor_type_enum.NATIONAL_ENERGY_LAW,
  } satisfies OrgPreferences;
}

/**
 * Provider for the public shared snapshot route. Renders a nested
 * `OrgPreferencesContext.Provider` so consumers in the shared subtree see
 * snapshot-backed values instead of the empty default the outer provider
 * left behind (its `GET_ORG_PREFERENCES` fails under the dummy session).
 */
export function SharedOrgPreferencesContextProvider({
  preferences,
  children,
}: PropsWithChildren<{ preferences: SharedSnapshotOrgPreferences | null }>) {
  const value = useMemo(() => {
    if (!preferences) {
      return initialState;
    }
    return {
      ...initialState,
      orgPreferences: buildOrgPreferencesFromSnapshot(preferences),
      isLoading: false,
    };
  }, [preferences]);

  return <OrgPreferencesContext.Provider value={value}>{children}</OrgPreferencesContext.Provider>;
}

export { OrgPreferencesContext, OrgPreferencesContextProvider };
